/* eslint-disable react-hooks/exhaustive-deps */
/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */
//Standard libraries
import { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";
import { Grid } from "@material-ui/core";
import MenuItem from "@material-ui/core/MenuItem";

import { ORDER_STATUS_FILTER } from "../constants/order";

//custom libraries

import { Button } from "../components";

import { SearchFilterTitle } from "./../components/reports/search-filter-title";
import { SearchHeader } from "./../components/reports/search-header";
import TextField from "@material-ui/core/TextField";
import { FormInputLabel } from "../components/common/FormInputLabel";
import { useStoresHandler } from "../hooks/rest/use-stores-handler";
import { useFfmcentersHandler } from "../hooks/rest/use-ffmcenters-handler";
import { useOrderHandler } from "../hooks/rest";
import { PaginationI } from "../interfaces/pagination.interface";
import { isAdminUser, isSellerUser } from "../helpers/user-session";
import OrderReportsModal from "../components/order/OrderReportsModal";
import { hasReportOrderSummaryAccess, hasReportOrderItemsAccess } from "../helpers/entitlementHelper";

const OrderSearchForm: React.FC<any> = ({
  page,
  setPage,
  size,
  setOrders,
  setTotalOrders,
  refetchOrders,
  setRefetchOrders,
  setShowLoader,
}) => {
  const { t } = useTranslation();
  const isSeller = isSellerUser();

  // Filters
  const { getStoresByTenant } = useStoresHandler();
  const { getFfmcentersByStore } = useFfmcentersHandler();
  const { getOrdersPagination, getOrdersPaginationBySeller } = useOrderHandler();
  const getOrdersFunction = isSeller ? getOrdersPaginationBySeller : getOrdersPagination;

  const [stores, setStores] = useState<any[]>([]);
  const [ffmCenters, setFfmCenters] = useState<any[]>([]);

  // Params
  const [orderStateParam, setOrderStateParam] = useState<string>(ORDER_STATUS_FILTER[0].id);
  const [pagination, setPagination] = useState<PaginationI>();
  const [storeParam, setStoreParam] = useState<any>();
  const [ffmCenterParam, setFfmCenterParam] = useState<any>();
  const [orderNumber, setOrderNumber] = useState<string>();

  // Report
  const [openReportModal, setOpenReportModal] = useState<boolean>(false);

  useEffect(() => {
    if (pagination) {
      setPagination((prevPagination) => ({
        ...prevPagination,
        size: size,
        page: page,
      }));
    }
  }, [page, size]);

  useEffect(() => {
    if (pagination) {
      setShowLoader(true);
      const [respOrders] = getOrdersFunction(pagination);
      respOrders
        .then((resp) => {
          setOrders(resp.data.data);
          setTotalOrders(resp.data.total);
        })
        .finally(() => {
          setShowLoader(false);
        });
    }
  }, [pagination]);

  useEffect(() => {
    if (pagination && refetchOrders) {
      setShowLoader(true);
      const [respOrders] = getOrdersFunction(pagination);
      respOrders
        .then((resp) => {
          setOrders(resp.data.data);
          setTotalOrders(resp.data.total);
          setRefetchOrders(false);
        })
        .finally(() => {
          setShowLoader(false);
        });
    }
  }, [refetchOrders]);

  const hasOrderReportAccess = () => {
    return hasReportOrderSummaryAccess() || hasReportOrderItemsAccess();
  };

  const onSubmit = () => {
    setPage(1);
    const paginationParam: PaginationI = {
      page: 1,
      size,
      status: orderStateParam,
      ffmCenterId: ffmCenterParam && parseInt(ffmCenterParam),
      storeId: storeParam && parseInt(storeParam),
      orderNumber,
    };
    setPagination(paginationParam);
  };

  const onClear = () => {
    setOrderStateParam(ORDER_STATUS_FILTER[0].id);
    setPage(1);
    setStoreParam("");
    setFfmCenterParam("");
    setOrderNumber("");
    const paginationParam: PaginationI = {
      page: 1,
      size,
      status: ORDER_STATUS_FILTER[0].id,
      ffmCenterId: undefined,
      storeId: undefined,
      orderNumber: undefined,
    };
    setPagination(paginationParam);
  };

  useEffect(() => {
    onClear();
    const [respStores] = getStoresByTenant();
    respStores.then((resp) => {
      setStores(resp.data.data);
    });
  }, []);

  useEffect(() => {
    if (storeParam) {
      const [respFfmcenters] = getFfmcentersByStore(storeParam);

      respFfmcenters.then((resp) => {
        setFfmCenters(resp.data.data);
      });
    }
  }, [storeParam]);

  const handleOrderStateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOrderStateParam(event.target.value);
  };

  const handleOrderNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOrderNumber(event.target.value);
  };

  const handleOpenReportModal = () => {
    setOpenReportModal(true);
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <SearchHeader title={t("OrderManagement.SearchOrders")} />
        </Grid>

        <Grid item xs={12}>
          <SearchFilterTitle title={t("Report.SearchFilterTitle")} />
        </Grid>

        <Grid container>
          {/* Order Status */}
          <Grid item xs={12} sm={6} xl={3} md={3} className="caja-form">
            <FormInputLabel text={t("OrderManagement.OrderFilters.Status")} />
            <TextField
              select
              value={orderStateParam}
              variant="outlined"
              size="small"
              fullWidth
              onChange={handleOrderStateChange}>
              {ORDER_STATUS_FILTER.map((item: any) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {stores && stores.length > 1 && (
            <>
              {/* Store */}
              <Grid item xs={12} sm={6} xl={3} md={3} className="caja-form">
                <FormInputLabel text={t("OrderManagement.OrderFilters.Store")} />
                <TextField
                  select
                  value={storeParam || ""}
                  variant="outlined"
                  size="small"
                  fullWidth
                  onChange={(e) => setStoreParam(e.target.value)}>
                  {stores &&
                    stores.map((item: any) => (
                      <MenuItem key={item.storeId} value={item.storeId}>
                        {item.name}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>

              {/* FfmCenter */}
              <Grid item xs={12} sm={6} xl={4} md={4} className="caja-form">
                <FormInputLabel text={t("OrderManagement.OrderFilters.FfmCenter")} />
                <TextField
                  select
                  value={ffmCenterParam || ""}
                  variant="outlined"
                  size="small"
                  fullWidth
                  onChange={(e) => setFfmCenterParam(e.target.value)}
                  SelectProps={{
                    MenuProps: {
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          maxWidth: 300,
                          fontSize: "9px",
                        },
                      },
                    },
                  }}>
                  {ffmCenters.map((item: any) => (
                    <MenuItem key={item.fulfillmentCenterId} value={item.fulfillmentCenterId}>
                      {item.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </>
          )}

          {/* OrderNumber */}
          <Grid item xs={12} sm={6} xl={2} md={2} className="caja-form">
            <FormInputLabel text={t("OrderManagement.OrderFilters.OrderNumber")} />
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              value={orderNumber}
              onChange={handleOrderNumberChange}></TextField>
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12} sm={6} xl={3} md={3} className="caja-form">
            <Button type="button" value="Search" name="Search" onClick={onSubmit}>
              {t("OrderManagement.Search")}
            </Button>
            {(storeParam || ffmCenterParam || orderNumber || orderStateParam !== ORDER_STATUS_FILTER[0].id) && (
              <span style={{ marginLeft: "20px" }}>
                <Button type="button" variant="text" onClick={onClear}>
                  {t("OrderManagement.SearchClean")}
                </Button>
              </span>
            )}
          </Grid>
          <Grid item xs={12} sm={6} xl={3} md={3} className=""></Grid>
          <Grid item xs={12} sm={6} xl={3} md={3} className=""></Grid>
          <Grid item xs={12} sm={6} xl={3} md={3} className="order-report-container caja-form">
            {hasOrderReportAccess() && (
              <Button
                type="button"
                value="ReportOrderSummary"
                name="ReportOrderSummary"
                onClick={handleOpenReportModal}>
                {t("OrderManagement.Reports")}
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
      {hasOrderReportAccess() && (
        <OrderReportsModal
          stores={stores}
          open={openReportModal}
          setOpen={setOpenReportModal}
          setShowLoader={setShowLoader}></OrderReportsModal>
      )}
    </>
  );
};
export default OrderSearchForm;
